import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { actions, Form } from "react-redux-form";
import { useParams } from "react-router";
import Alert from "react-s-alert";
import ReactTable, { RowInfo } from "react-table";
import * as ReactTooltip from "react-tooltip";
import { bindActionCreators } from "redux";
import * as LaboratoryConfidentialityDeedActions from "../../../actions/laboratoryConfidentialityDeed";
import * as LaboratoryContractActions from "../../../actions/laboratoryContract";
import * as NavigationActions from "../../../actions/navigation";
import {
    InlineLoading,
    Modal,
    NotificationAlert,
} from "../../../components/common/index";
import {
    DatePickerFormInputFabric,
    SelectFormInput,
    TextFormInput,
} from "../../../components/form";
import { RestrictedLayout } from "../../../components/layouts/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import { PAGE_SIZE_OPTIONS } from "../../../constants/reactTableConstants";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { ModalSize } from "../../../enumerations/ModalSize";
import { RequestState } from "../../../enumerations/RequestState";
import { isUrl } from "../../../functions/string";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { LaboratoryContractBreadcrumbs } from "../../../helpers/breadcrumbHelper";
import {
    convertToApproximateDateString,
    convertToShortDateString,
} from "../../../helpers/date";
import {
    RegistrationComponent,
    renderFormControls,
} from "../../../helpers/formHelper";
import {
    findLookup,
    generateOptionsFromLookup,
} from "../../../helpers/lookupHelper";
import { LaboratorySubMenu } from "../../../helpers/subMenuHelper";
import useNavigate from "../../../hooks/useNavigate";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";
import { ILaboratoryConfidentialityDeedReduxFormState } from "../../../reducers/reactReduxForms/laboratoryConfidentialityDeed";
import { ILaboratoryContractReduxFormState } from "../../../reducers/reactReduxForms/laboratoryContract";

import "./LabContractPage.scss";

interface LaboratoryContractPageProps {
    // Laboratory Contract
    form: Dtos.LaboratoryContract & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    reduxFormState: ILaboratoryContractReduxFormState;
    laboratoryContract: Dtos.LaboratoryContract;
    lookups: Dtos.Lookup[];
    loadingLaboratoryContract: boolean;
    loadLaboratoryContractSuccess: boolean;
    loadLaboratoryContractFailure: boolean;
    loadLaboratoryContractErrorCode: string;
    savingLaboratoryContract: boolean;
    saveLaboratoryContractSuccess: boolean;
    saveLaboratoryContractFailure: boolean;
    validatingLaboratoryContract: boolean;
    validationFailures: Dtos.ResponseError[];
    clearLaboratoryContract: LaboratoryContractActions.ILaboratoryContractClearActionCreator;
    loadLaboratoryContract: LaboratoryContractActions.ILaboratoryContractLoadByLaboratoryIdActionCreator;
    saveLaboratoryContract: LaboratoryContractActions.ILaboratoryContractSaveActionCreator;
    validateLaboratoryContract: LaboratoryContractActions.ILaboratoryContractValidateActionCreator;

    // Laboratory Confidentiality Deed
    laboratoryConfidentialityDeedForm: Dtos.LaboratoryConfidentialityDeed & IRegistrationFormState;
    laboratoryConfidentialityDeedFormState: Dtos.FormState;
    laboratoryConfidentialityDeedFormProperties: Dtos.FormProperty[];
    laboratoryConfidentialityDeedReduxFormState: ILaboratoryConfidentialityDeedReduxFormState;
    laboratoryConfidentialityDeed: Dtos.LaboratoryConfidentialityDeed;
    laboratoryConfidentialityDeedLookups: Dtos.Lookup[];
    loadingLaboratoryConfidentialityDeed: boolean;
    loadLaboratoryConfidentialityDeedSuccess: boolean;
    loadLaboratoryConfidentialityDeedFailure: boolean;
    savingLaboratoryConfidentialityDeed: boolean;
    saveLaboratoryConfidentialityDeedSuccess: boolean;
    saveLaboratoryConfidentialityDeedFailure: boolean;
    validatingLaboratoryConfidentialityDeed: boolean;
    creatingLaboratoryConfidentialityDeed: boolean;
    createLaboratoryConfidentialityDeedSuccess: boolean;
    createLaboratoryConfidentialityDeedFailure: boolean;
    deletingLaboratoryConfidentialityDeed: boolean;
    deleteLaboratoryConfidentialityDeedSuccess: boolean;
    laboratoryConfidentialityDeedValidationFailures: Dtos.ResponseError[];
    clearLaboratoryConfidentialityDeed: LaboratoryConfidentialityDeedActions.ILaboratoryConfidentialityDeedClearActionCreator;
    loadLaboratoryConfidentialityDeed: LaboratoryConfidentialityDeedActions.ILaboratoryConfidentialityDeedLoadByIdActionCreator;
    saveLaboratoryConfidentialityDeed: LaboratoryConfidentialityDeedActions.ILaboratoryConfidentialityDeedSaveActionCreator;
    createLaboratoryConfidentialityDeed: LaboratoryConfidentialityDeedActions.ILaboratoryConfidentialityDeedCreateActionCreator;
    deleteLaboratoryConfidentialityDeed: LaboratoryConfidentialityDeedActions.ILaboratoryConfidentialityDeedDeleteActionCreator;
    validateLaboratoryConfidentialityDeed: LaboratoryConfidentialityDeedActions.ILaboratoryConfidentialityDeedValidateActionCreator;

    // Common
    user: Dtos.User;
    permissions?: Dtos.PersonnelTmdPermissions[];
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const reduxFormName: string = "reduxForms.laboratoryContract";
const laboratoryConfidentialityDeedReduxFormName: string = "reduxForms.laboratoryConfidentialityDeed";

const LaboratoryContractPage: FC<LaboratoryContractPageProps> = ({
    form,
    formState,
    formProperties,
    laboratoryContract,
    lookups,
    loadingLaboratoryContract,
    loadLaboratoryContractFailure,
    loadLaboratoryContractErrorCode,
    savingLaboratoryContract,
    saveLaboratoryContractSuccess,
    saveLaboratoryContractFailure,
    validatingLaboratoryContract,
    validationFailures,
    clearLaboratoryContract,
    loadLaboratoryContract,
    saveLaboratoryContract,
    validateLaboratoryContract,

    laboratoryConfidentialityDeedForm,
    laboratoryConfidentialityDeedFormState,
    laboratoryConfidentialityDeedFormProperties,
    laboratoryConfidentialityDeed,
    laboratoryConfidentialityDeedLookups,
    loadingLaboratoryConfidentialityDeed,
    savingLaboratoryConfidentialityDeed,
    saveLaboratoryConfidentialityDeedSuccess,
    saveLaboratoryConfidentialityDeedFailure,
    creatingLaboratoryConfidentialityDeed,
    deletingLaboratoryConfidentialityDeed,
    deleteLaboratoryConfidentialityDeedSuccess,
    validatingLaboratoryConfidentialityDeed,
    laboratoryConfidentialityDeedValidationFailures,
    clearLaboratoryConfidentialityDeed,
    loadLaboratoryConfidentialityDeed,
    saveLaboratoryConfidentialityDeed,
    createLaboratoryConfidentialityDeed,
    deleteLaboratoryConfidentialityDeed,
    validateLaboratoryConfidentialityDeed,

    permissions,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { laboratoryId } = useParams<{ laboratoryId: string }>();
    const laboratoryIdNumber = parseInt(laboratoryId, 10);

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [formInitalised, setFormInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [laboratoryConfidentialityDeedId, setLaboratoryConfidentialityDeedId] = useState<number | null>(null);
    const [formInitalisedLaboratoryConfidentialityDeed, setFormInitalisedLaboratoryConfidentialityDeed] = useState<boolean>(false);
    const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
    const [hasBeenSubmittedConfidentalityDeed, setHasBeenSubmittedConfidentalityDeed] = useState<boolean>(false);

    //
    // -- Hooks --
    const navigate = useNavigate();

    const loadReduxForm = () => {
        let extraData: Dtos.LaboratoryContract = {} as Dtos.LaboratoryContract;

        loadForm(
            reduxFormName,
            Object.assign({ ...laboratoryContract }, extraData)
        );
    };

    const loadLaboratoryConfidentialityDeedReduxForm = () => {
        let extraData: Dtos.LaboratoryConfidentialityDeed = {} as Dtos.LaboratoryConfidentialityDeed;

        loadForm(
            laboratoryConfidentialityDeedReduxFormName,
            Object.assign({ ...laboratoryConfidentialityDeed }, extraData)
        );
    };


    // Load Laboratory Contract
    useEffect(() => {
        if (laboratoryIdNumber && !laboratoryContract) {
            setBreadcrumbs(LaboratoryContractBreadcrumbs(laboratoryIdNumber));
            setDrawerItems(LaboratorySubMenu(laboratoryIdNumber, permissions));

            loadLaboratoryContract(laboratoryIdNumber);
        } else if (laboratoryContract && !formInitalised) {
            loadReduxForm();
            setFormInitalised(true);
        }
    }, [
        laboratoryIdNumber,
        laboratoryContract,
        permissions,
        formInitalised,
        setDrawerItems,
        setBreadcrumbs,
        loadLaboratoryContract,
        LaboratoryContractBreadcrumbs,
        setFormInitalised,
    ]);

    // Load Laboratory Confidentiality Deed
    useEffect(() => {
        if (isOpen) {
            if (
                laboratoryConfidentialityDeedId &&
                !laboratoryConfidentialityDeed &&
                !loadingLaboratoryConfidentialityDeed
            ) {
                loadLaboratoryConfidentialityDeed(laboratoryConfidentialityDeedId);
            } else if (
                !laboratoryConfidentialityDeedId &&
                !laboratoryConfidentialityDeed &&
                !creatingLaboratoryConfidentialityDeed
            ) {
                createLaboratoryConfidentialityDeed(laboratoryIdNumber);
            } else if (
                laboratoryConfidentialityDeed &&
                !formInitalisedLaboratoryConfidentialityDeed
            ) {
                loadLaboratoryConfidentialityDeedReduxForm();
                setFormInitalisedLaboratoryConfidentialityDeed(true);
            }
        }
    }, [
        isOpen,
        laboratoryConfidentialityDeedId,
        laboratoryConfidentialityDeed,
        formInitalisedLaboratoryConfidentialityDeed,
        loadLaboratoryConfidentialityDeed,
        setFormInitalisedLaboratoryConfidentialityDeed,
        loadLaboratoryConfidentialityDeedReduxForm,
        loadingLaboratoryConfidentialityDeed,
        laboratoryIdNumber,
        createLaboratoryConfidentialityDeed,
        creatingLaboratoryConfidentialityDeed,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadLaboratoryContractFailure) {
            navigate(`/error/${loadLaboratoryContractErrorCode}`);
        }
    }, [
        loadLaboratoryContractFailure,
        loadLaboratoryContractErrorCode,
        navigate,
    ]);

    // Reload laboratory contract & confidentiality deed after delete
    useEffect(() => {
        if (
            !deletingLaboratoryConfidentialityDeed &&
            deleteLaboratoryConfidentialityDeedSuccess
        ) {
            clearLaboratoryContract();
            resetForm(reduxFormName);
            setFormInitalised(false);
            clearLaboratoryConfidentialityDeed();
            setLaboratoryConfidentialityDeedId(null);
            setIsOpen(false);
            resetForm(laboratoryConfidentialityDeedReduxFormName);
            setFormInitalisedLaboratoryConfidentialityDeed(false);
        }
    }, [
        deletingLaboratoryConfidentialityDeed,
        deleteLaboratoryConfidentialityDeedSuccess,
        loadLaboratoryConfidentialityDeed,
        loadLaboratoryContract,
        laboratoryIdNumber,
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (laboratoryIdNumber && laboratoryContract?.laboratory) {
            setBreadcrumbs(
                LaboratoryContractBreadcrumbs(
                    laboratoryIdNumber,
                    laboratoryContract.laboratory?.name
                )
            );
        }
    }, [
        laboratoryIdNumber,
        laboratoryContract,
        setBreadcrumbs,
        LaboratoryContractBreadcrumbs,
    ]);

    // Reload laboratory contract after saving
    useEffect(() => {
        if (!savingLaboratoryContract && saveLaboratoryContractSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Laboratory contract saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                navigate(`/labs`);
            } else if (!laboratoryIdNumber) {
                navigate(`/labs/${laboratoryIdNumber}/contract`);
                clearLaboratoryContract();
            }
        }
    }, [
        savingLaboratoryContract,
        saveLaboratoryContractSuccess,
        laboratoryIdNumber,
        navigate,
        saveAndReturn,
        clearLaboratoryContract,
    ]);

    // Failed saving laboratory contract
    useEffect(() => {
        if (!savingLaboratoryContract && saveLaboratoryContractFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [savingLaboratoryContract, saveLaboratoryContractSuccess]);

    // Reload laboratory confidentiality after saving
    useEffect(() => {
        if (
            !savingLaboratoryConfidentialityDeed &&
            saveLaboratoryConfidentialityDeedSuccess
        ) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Laboratory confidentiality saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            clearLaboratoryContract();
            resetForm(reduxFormName);
            setFormInitalised(false);
            clearLaboratoryConfidentialityDeed();
            setLaboratoryConfidentialityDeedId(null);
            setIsOpen(false);
            resetForm(laboratoryConfidentialityDeedReduxFormName);
            setFormInitalisedLaboratoryConfidentialityDeed(false);
        }
    }, [
        savingLaboratoryConfidentialityDeed,
        saveLaboratoryConfidentialityDeedSuccess,
        setIsOpen,
        clearLaboratoryContract,
        clearLaboratoryConfidentialityDeed,
        setLaboratoryConfidentialityDeedId,
        resetForm,
        reduxFormName,
        laboratoryConfidentialityDeedReduxFormName,
    ]);

    // Failed saving laboratory confidentiality deed
    useEffect(() => {
        if (
            !savingLaboratoryConfidentialityDeed &&
            saveLaboratoryConfidentialityDeedFailure
        ) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [
        savingLaboratoryConfidentialityDeed,
        saveLaboratoryConfidentialityDeedFailure,
    ]);

    // Cleanup effect to clear the laboratory contract and confidentiality deed on component unmount
    useEffect(() => {
        return () => {
            clearLaboratoryContract();
            clearLaboratoryConfidentialityDeed();
            resetForm(reduxFormName);
            resetForm(laboratoryConfidentialityDeedReduxFormName);
        };
    }, [
        clearLaboratoryContract,
        clearLaboratoryConfidentialityDeed,
        resetForm,
        reduxFormName,
        laboratoryConfidentialityDeedReduxFormName,
    ]);

    // Laboratory contract submit
    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.LaboratoryContract = {} as Dtos.LaboratoryContract;

            if (!data.laboratoryId) {
                changes.laboratoryId = laboratoryIdNumber;
            }

            await saveLaboratoryContract(Object.assign({ ...data }, changes));

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    // Laboratory contract submit failed
    const handleOnSubmitFailed = (_data: Dtos.LaboratoryContract) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    // Laboratory confidentiality deed submit
    const handleOnSubmitLaboratoryConfidentialityDeed = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmittedConfidentalityDeed(true);
        }

        if (data) {
            let changes: Dtos.LaboratoryConfidentialityDeed = {} as Dtos.LaboratoryConfidentialityDeed;

            if (!data.laboratoryId) {
                changes.laboratoryId = laboratoryIdNumber;
            }

            await saveLaboratoryConfidentialityDeed(
                Object.assign({ ...data }, changes)
            );

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    // Laboratory confidentiality deed submit failed
    const handleOnSubmitFailedLaboratoryConfidentialityDeed = (_data: Dtos.LaboratoryConfidentialityDeed) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const onCancel = () => {
        navigate("/labs");
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.LaboratoryContract = {} as Dtos.LaboratoryContract;

            if (form && hasBeenSubmitted && !validatingLaboratoryContract) {
                await validateLaboratoryContract(
                    Object.assign({ ...form }, changes)
                );
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidateConfidentialityDeed = debounce(async () => {
            let changes: Dtos.LaboratoryConfidentialityDeed = {} as Dtos.LaboratoryConfidentialityDeed;

            if (
                laboratoryConfidentialityDeedForm &&
                hasBeenSubmittedConfidentalityDeed &&
                !validatingLaboratoryConfidentialityDeed
            ) {
                await validateLaboratoryConfidentialityDeed(
                    Object.assign(
                        { ...laboratoryConfidentialityDeedForm },
                        changes
                    )
                );
            }
        }, 250);

        debounceValidateConfidentialityDeed();

        return () => {
            debounceValidateConfidentialityDeed.cancel();
        };
    }, [laboratoryConfidentialityDeedForm]);

    const openLink = (link: string) => {
        window.open(`${link}`, "_blank");
        return null;
    };

    const generateDeleteButton = () => {
        const hasPermission = AuthenticationHelper.hasPermission(
            permissions,
            Dtos.Permission.LabConfidentialityDeedUpdate,
            undefined,
            undefined
        );

        if (!hasPermission) return null;

        return (
            <button
                key={`laboratoryConfidentialityDeed-DeleteButton-${laboratoryContract?.laboratoryConfidentialityDeed?.id}`}
                className="btn btn-outline-secondary btn-sm btn-row-version"
                onClick={(event) => {
                    event.stopPropagation();
                    event.preventDefault();
                    setOpenDeleteModal(true);
                }}
            >
                <FontAwesomeIcon icon={FontAwesomeIcons.Duotone.TRASH} fixedWidth />
            </button>
        );
    };

    const renderDeleteConfirmation = useMemo(() => {
        const hasPermission = AuthenticationHelper.hasPermission(
            permissions,
            Dtos.Permission.LabConfidentialityDeedUpdate,
            undefined,
            undefined
        );

        if (hasPermission) {
            return (
                <Modal
                    open={openDeleteModal}
                    disableCloseOnOverlayClick={true}
                    onClose={() => {
                        setOpenDeleteModal(false);
                    }}
                    size={ModalSize.Md}
                >
                    <div className="modal-header">
                        <div
                            className="modal-title"
                            style={{ minWidth: "540px" }}
                        >
                            <h2 className="mb-0">Delete</h2>
                        </div>
                        <button
                            type="button"
                            className="close"
                            aria-label="Close"
                            onClick={() => {
                                setOpenDeleteModal(false);
                            }}
                        >
                            <FontAwesomeIcon fixedWidth icon={FontAwesomeIcons.Regular.TIMES} />
                        </button>
                    </div>
                    <div className="modal-body">
                        <span>
                            Are you sure you want to delete the confidentiality
                            deed "
                            <em>
                                {
                                    laboratoryContract
                                        ?.laboratoryConfidentialityDeed?.name
                                }
                                "
                            </em>{" "}
                            from laboratory{" "}
                            <strong>
                                {laboratoryContract?.laboratory?.name}
                            </strong>
                            .
                        </span>
                    </div>
                    <div className="modal-footer">
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => {
                                deleteLaboratoryConfidentialityDeed(laboratoryContract?.laboratoryConfidentialityDeed?.id);
                                setOpenDeleteModal(false);
                            }}
                        >
                            Delete
                        </button>
                        <button
                            type="button"
                            className="btn btn-outline-primary"
                            onClick={() => {
                                setOpenDeleteModal(false);
                            }}
                        >
                            Cancel
                        </button>
                    </div>
                </Modal>
            );
        }
        return null;
    }, [
        openDeleteModal,
        permissions,
        setOpenDeleteModal,
        laboratoryIdNumber,
        laboratoryConfidentialityDeed,
        laboratoryContract,
        deleteLaboratoryConfidentialityDeed,
    ]);

    const renderConfidentialityDeedModal = useMemo(() => {
        const hasPermission = AuthenticationHelper.hasAnyPermission(
            permissions,
            [
                Dtos.Permission.LabConfidentialityDeedUpdate,
                Dtos.Permission.LabConfidentialityDeedCreate,
            ]
        );

        if (hasPermission) {
            const formDisabled: boolean =
                !hasPermission ||
                savingLaboratoryConfidentialityDeed ||
                (laboratoryConfidentialityDeedFormState &&
                    laboratoryConfidentialityDeedFormState.formStatus ==
                        Dtos.FormStatus.Disabled);

            let formComponents: { [index: string]: RegistrationFormComponent } =
                {
                    detailsGroup: {
                        type: RegistrationComponent.RegistrationElement,
                        component: (
                            <>
                                <div className="form-row pt-4 pb-2 px-4">
                                    <div className="col-12">
                                        {renderFormControls(
                                            laboratoryConfidentialityDeedForm,
                                            "laboratoryConfidentialityDeed",
                                            {
                                                name: {
                                                    type: RegistrationComponent.RegistrationControl,
                                                    inputType: TextFormInput,
                                                    inputDisabled: formDisabled,
                                                    groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                    labelClassName: "col-form-label col-sm-12 col-xl-2",
                                                    fieldClassName: "col-sm-12 col-xl-10 generic-form-field",
                                                },
                                            },
                                            laboratoryConfidentialityDeedLookups,
                                            laboratoryConfidentialityDeedValidationFailures,
                                            laboratoryConfidentialityDeedFormProperties,
                                            changeForm
                                        )}
                                    </div>
                                </div>
                                <div className="form-row py-2 px-4">
                                    <div className="col-12">
                                        {renderFormControls(
                                            laboratoryConfidentialityDeedForm,
                                            "laboratoryConfidentialityDeed",
                                            {
                                                location: {
                                                    type: RegistrationComponent.RegistrationControl,
                                                    inputType: TextFormInput,
                                                    inputDisabled: formDisabled,
                                                    inputProps: {
                                                        addOnRightButton: (
                                                            <button
                                                                className="btn btn-secondary"
                                                                type="button"
                                                                id="button-addon2"
                                                                disabled={laboratoryConfidentialityDeedForm?.location == null || !isUrl(laboratoryConfidentialityDeedForm?.location)}
                                                                style={{
                                                                    cursor: laboratoryConfidentialityDeedForm?.location == null || !isUrl(laboratoryConfidentialityDeedForm?.location)
                                                                            ? "not-allowed"
                                                                            : "pointer",
                                                                    padding: "4px 10px",
                                                                }}
                                                                onClick={() => {
                                                                    openLink(laboratoryConfidentialityDeedForm.location);
                                                                }}
                                                            >
                                                                <FontAwesomeIcon icon={FontAwesomeIcons.Duotone.ARROWUPRIGHTFROMSQUARE} />
                                                            </button>
                                                        ),
                                                    },
                                                    groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                    labelClassName: "col-form-label col-sm-12 col-xl-2",
                                                    fieldClassName: "col-sm-12 col-xl-10 generic-form-field",
                                                },
                                            },
                                            laboratoryConfidentialityDeedLookups,
                                            laboratoryConfidentialityDeedValidationFailures,
                                            laboratoryConfidentialityDeedFormProperties,
                                            changeForm
                                        )}
                                    </div>
                                </div>
                                <div className="form-row pt-2 pb-4 px-4">
                                    <div className="col-12 col-xl-6">
                                        {renderFormControls(
                                            laboratoryConfidentialityDeedForm,
                                            "laboratoryConfidentialityDeed",
                                            {
                                                dateOfDeed: {
                                                    type: RegistrationComponent.RegistrationControl,
                                                    inputType: DatePickerFormInputFabric,
                                                    inputDisabled: formDisabled,
                                                    groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                    labelClassName: "col-form-label col-sm-12 col-xl-4",
                                                    fieldClassName: "col-sm-12 col-xl-7 generic-form-field",
                                                },
                                            },
                                            laboratoryConfidentialityDeedLookups,
                                            laboratoryConfidentialityDeedValidationFailures,
                                            laboratoryConfidentialityDeedFormProperties,
                                            changeForm
                                        )}
                                    </div>
                                    <div className="col-12 col-xl-6">
                                        {renderFormControls(
                                            laboratoryConfidentialityDeedForm,
                                            "laboratoryConfidentialityDeed",
                                            {
                                                dateFullyExecuted: {
                                                    type: RegistrationComponent.RegistrationControl,
                                                    inputType: DatePickerFormInputFabric,
                                                    inputDisabled: formDisabled,
                                                    groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                    labelClassName: "col-form-label col-sm-12 col-xl-4",
                                                    fieldClassName: "col-sm-12 col-xl-8 generic-form-field",
                                                },
                                            },
                                            laboratoryConfidentialityDeedLookups,
                                            laboratoryConfidentialityDeedValidationFailures,
                                            laboratoryConfidentialityDeedFormProperties,
                                            changeForm
                                        )}
                                    </div>
                                </div>
                            </>
                        ),
                    },
                };

            return (
                <Modal
                    open={isOpen}
                    disableCloseOnOverlayClick={true}
                    onClose={() => {
                        setIsOpen(false);
                        setLaboratoryConfidentialityDeedId(null);
                        clearLaboratoryConfidentialityDeed();
                    }}
                    size={ModalSize.Xl}
                >
                    <div className="modal-header">
                        <div className="modal-title">
                            <h2 className="mb-0">Confidentiality Deed</h2>
                        </div>
                        <button
                            type="button"
                            className="close"
                            aria-label="Close"
                            onClick={() => {
                                setIsOpen(false);
                                setLaboratoryConfidentialityDeedId(null);
                                clearLaboratoryConfidentialityDeed();
                            }}
                        >
                            <FontAwesomeIcon fixedWidth icon={FontAwesomeIcons.Regular.TIMES} />
                        </button>
                    </div>
                    <div className="modal-body" style={{ minHeight: "280px" }}>
                        {loadingLaboratoryConfidentialityDeed || creatingLaboratoryConfidentialityDeed ? (
                            <div className="d-flex h-100 align-items-center justify-content-center">
                                <InlineLoading />
                            </div>
                        ) : (
                            <Form
                                model={laboratoryConfidentialityDeedReduxFormName}
                                onSubmit={(val, _event) =>
                                    handleOnSubmitLaboratoryConfidentialityDeed(val)
                                }
                                onSubmitFailed={(val) =>
                                    handleOnSubmitFailedLaboratoryConfidentialityDeed(val)
                                }
                            >
                                {renderFormControls(
                                    laboratoryConfidentialityDeedForm,
                                    "laboratoryConfidentialityDeed",
                                    formComponents,
                                    laboratoryConfidentialityDeedLookups,
                                    laboratoryConfidentialityDeedValidationFailures,
                                    laboratoryConfidentialityDeedFormProperties,
                                    changeForm
                                )}
                                <div className="mt-4 mb-4 text-right">
                                    <button
                                        type="submit"
                                        disabled={formDisabled}
                                        className="btn btn-primary mr-2"
                                        onClick={() => {
                                            changeForm(
                                                laboratoryConfidentialityDeedReduxFormName + ".saveAndReturn",
                                                false
                                            );
                                        }}
                                    >
                                        Submit
                                    </button>
                                    <button
                                        className="btn btn-primary mr-2"
                                        onClick={() => {
                                            setIsOpen(false);
                                            setLaboratoryConfidentialityDeedId(null);
                                            clearLaboratoryConfidentialityDeed();
                                        }}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </Form>
                        )}
                    </div>
                </Modal>
            );
        }
        return null;
    }, [
        isOpen,
        permissions,
        setIsOpen,
        loadingLaboratoryConfidentialityDeed,
        laboratoryConfidentialityDeedReduxFormName,
        handleOnSubmitLaboratoryConfidentialityDeed,
        handleOnSubmitFailedLaboratoryConfidentialityDeed,
        savingLaboratoryConfidentialityDeed,
        laboratoryConfidentialityDeedFormState,
        laboratoryConfidentialityDeedForm,
        openLink,
        setLaboratoryConfidentialityDeedId,
        clearLaboratoryConfidentialityDeed,
        creatingLaboratoryConfidentialityDeed,
        changeForm,
        laboratoryConfidentialityDeedFormProperties,
        laboratoryConfidentialityDeedValidationFailures,
        laboratoryConfidentialityDeedLookups,
    ]);

    const renderConfidentialityDeedContent = useMemo(() => {
        const hasPermission = AuthenticationHelper.hasAnyPermission(
            permissions,
            [
                Dtos.Permission.LabConfidentialityDeedUpdate,
                Dtos.Permission.LabConfidentialityDeedCreate,
            ]
        );

        if (
            form?.laboratoryConfidentialityDeed &&
            form?.laboratoryConfidentialityDeed?.id
        ) {
            return (
                <div className="card card-shadow" style={{ zIndex: 1 }}>
                    <div className="table-wrapper">
                        <ReactTable
                            data={[form.laboratoryConfidentialityDeed]}
                            pageSizeOptions={PAGE_SIZE_OPTIONS}
                            defaultPageSize={1}
                            pageSize={1}
                            showPagination={false}
                            className="-striped -highlight -clickable"
                            noDataText={`${form?.laboratory?.name} has no confidentiality deed to view.`}
                            resizable={false}
                            onPageSizeChange={(_val) => {}}
                            getTdProps={(
                                _state,
                                rowInfo: RowInfo,
                                _column,
                                _instance
                            ) => {
                                return {
                                    onClick: (_e, handleOriginal) => {
                                        if (hasPermission) {
                                            setIsOpen(true);
                                            setLaboratoryConfidentialityDeedId(rowInfo.original.id);
                                        }

                                        if (handleOriginal) {
                                            handleOriginal();
                                        }
                                    },
                                    style: {
                                        color:
                                            rowInfo &&
                                            rowInfo.original &&
                                            rowInfo.original.active
                                                ? "#212529"
                                                : "#9e9e9e",
                                    },
                                };
                            }}
                            defaultSorted={[
                                {
                                    id: "id",
                                    desc: false,
                                },
                            ]}
                            columns={[
                                {
                                    id: "id",
                                    Header: " ",
                                    accessor: "id",
                                    maxWidth: 50,
                                    Cell: (_props) => (
                                        <FontAwesomeIcon size="lg" icon={FontAwesomeIcons.Duotone.SIGNATURE} fixedWidth />
                                    ),
                                },
                                {
                                    id: "name",
                                    Header: "Name",
                                    accessor: "name",
                                },
                                {
                                    id: "dateOfDeed",
                                    Header: "Date Of Deed",
                                    accessor: "dateOfDeed",
                                    maxWidth: 250,
                                    Cell: (props) => (
                                        <div>
                                            <span
                                                data-tip={convertToApproximateDateString(props.value)}
                                            >
                                                {convertToShortDateString(props.value)}
                                            </span>
                                            <ReactTooltip />
                                        </div>
                                    ),
                                },
                                {
                                    id: "dateFullyExecuted",
                                    Header: "Date Fully Executed",
                                    accessor: "dateFullyExecuted",
                                    maxWidth: 250,
                                    Cell: (props) => (
                                        <div>
                                            <span
                                                data-tip={convertToApproximateDateString(props.value)}
                                            >
                                                {convertToShortDateString(props.value)}
                                            </span>
                                            <ReactTooltip />
                                        </div>
                                    ),
                                },
                                {
                                    id: "delete",
                                    Header: " ",
                                    maxWidth: 75,
                                    Cell: (_props) => (
                                        <div>{generateDeleteButton()}</div>
                                    ),
                                },
                            ]}
                        />
                    </div>
                </div>
            );
        } else {
            return (
                <div className="card card-shadow p-3" style={{ zIndex: 1 }}>
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon icon={FontAwesomeIcons.Solid.INFO_SQUARE} fixedWidth />
                        </div>
                        <span>
                            <i>{form?.laboratory?.name}</i> has no confidentiality deed to view.
                        </span>
                    </div>
                    {hasPermission && (
                        <div className="mt-3 d-flex justify-content-end">
                            <button
                                type="button"
                                onClick={() => {
                                    setIsOpen(true);
                                }}
                                className="btn btn-primary"
                            >
                                Add
                            </button>
                        </div>
                    )}
                </div>
            );
        }
    }, [
        form,
        setIsOpen,
        PAGE_SIZE_OPTIONS,
        convertToApproximateDateString,
        convertToShortDateString,
        setLaboratoryConfidentialityDeedId,
        permissions,
    ]);

    const renderContent = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermissionFor(
                laboratoryIdNumber,
                null,
                permissions,
                Dtos.Permission.LabContractUpdate,
                Dtos.Permission.LabContractCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingLaboratoryContract ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            detailsGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <h1>Details</h1>
                        <div className="card card-shadow" style={{ zIndex: 1 }}>
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-12">
                                    {renderFormControls(
                                        form,
                                        "laboratoryContract",
                                        {
                                            agreementName: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-sm-12 col-xl-2",
                                                fieldClassName: "col-sm-12 col-xl-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-12 col-xl-6">
                                    {renderFormControls(
                                        form,
                                        "laboratoryContract",
                                        {
                                            noOfOriginalCTRARequired: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-sm-12 col-xl-4",
                                                fieldClassName: "col-sm-12 col-xl-7 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-12 col-xl-6">
                                    {renderFormControls(
                                        form,
                                        "laboratoryContract",
                                        {
                                            laboratoryContractSiteTypeId: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: SelectFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    options:
                                                        generateOptionsFromLookup(
                                                            findLookup(
                                                                "LaboratoryContract",
                                                                "laboratoryContractSiteTypeId",
                                                                lookups
                                                            ),
                                                            form.laboratoryContractSiteTypeId
                                                        ),
                                                },
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-sm-12 col-xl-4",
                                                fieldClassName: "col-sm-12 col-xl-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
            confidentialityDeedGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <br />
                        <h1>Confidentiality Deed</h1>
                        {renderConfidentialityDeedContent}
                    </>
                ),
            },
        };

        return (
            <Form
                model={reduxFormName}
                onSubmit={(val, _event) => handleOnSubmit(val)}
                onSubmitFailed={(val) => handleOnSubmitFailed(val)}
            >
                {renderFormControls(
                    form,
                    "laboratoryContract",
                    formComponents,
                    lookups,
                    validationFailures,
                    formProperties,
                    changeForm
                )}
                <div className="mt-4 mb-4 text-right">
                    <button
                        type="submit"
                        className="btn btn-primary mr-2"
                        disabled={formDisabled}
                        onClick={() => {
                            changeForm(reduxFormName + ".saveAndReturn", false);
                        }}
                    >
                        Submit
                    </button>
                    <button
                        type="submit"
                        className="btn btn-primary mr-2"
                        disabled={formDisabled}
                        onClick={() => {
                            changeForm(reduxFormName + ".saveAndReturn", true);
                        }}
                    >
                        Submit And Return
                    </button>
                    <button className="btn btn-primary mr-2" onClick={onCancel}>
                        Cancel
                    </button>
                </div>
            </Form>
        );
    }, [
        AuthenticationHelper,
        permissions,
        savingLaboratoryContract,
        formState,
        handleOnSubmit,
        handleOnSubmitFailed,
        renderFormControls,
        lookups,
        validationFailures,
        formProperties,
        form,
        changeForm,
        reduxFormName,
        onCancel,
        laboratoryContract,
        renderConfidentialityDeedContent,
        laboratoryIdNumber,
    ]);

    return (
        <RestrictedLayout
            permission={Dtos.Permission.TrialGroupLabView}
            loading={loadingLaboratoryContract || savingLaboratoryContract}
        >
            {renderContent}
            {renderConfidentialityDeedModal}
            {renderDeleteConfirmation}
        </RestrictedLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<LaboratoryContractPageProps> => {
    let laboratoryContract: Dtos.LaboratoryContract | undefined = !(
        state.laboratoryContract.formData instanceof Array
    )
        ? state.laboratoryContract.formData
        : undefined;
    let laboratoryConfidentialityDeed:
        | Dtos.LaboratoryConfidentialityDeed
        | undefined = !(
        state.laboratoryConfidentialityDeed.formData instanceof Array
    )
        ? state.laboratoryConfidentialityDeed.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        laboratoryContract: laboratoryContract!,
        loadingLaboratoryContract: state.laboratoryContract.loadState && state.laboratoryContract.loadState.status == RequestState.Pending,
        loadLaboratoryContractSuccess: state.laboratoryContract.loadState && state.laboratoryContract.loadState.status == RequestState.Success,
        loadLaboratoryContractFailure: state.laboratoryContract.loadState && state.laboratoryContract.loadState.status == RequestState.Failure,
        loadLaboratoryContractErrorCode: state.laboratoryContract.loadState.errorCode,
        savingLaboratoryContract: state.laboratoryContract.saveState && state.laboratoryContract.saveState.status == RequestState.Pending,
        saveLaboratoryContractSuccess: state.laboratoryContract.saveState && state.laboratoryContract.saveState.status == RequestState.Success,
        saveLaboratoryContractFailure: state.laboratoryContract.saveState && state.laboratoryContract.saveState.status == RequestState.Failure,
        validatingLaboratoryContract: state.laboratoryContract.validateState && state.laboratoryContract.validateState.status == RequestState.Pending,
        lookups: state.laboratoryContract.lookups,
        form: state.reduxForms.laboratoryContract,
        formState: state.laboratoryContract.formState,
        formProperties: state.laboratoryContract.formProperties,
        reduxFormState: state.reduxForms.formState.laboratoryContract,
        validationFailures: laboratoryContract
            ? state.laboratoryContract.validationFailures
            : undefined,

        laboratoryConfidentialityDeed: laboratoryConfidentialityDeed!,
        loadingLaboratoryConfidentialityDeed: state.laboratoryConfidentialityDeed.loadState && state.laboratoryConfidentialityDeed.loadState.status == RequestState.Pending,
        loadLaboratoryConfidentialityDeedSuccess: state.laboratoryConfidentialityDeed.loadState && state.laboratoryConfidentialityDeed.loadState.status == RequestState.Success,
        loadLaboratoryConfidentialityDeedFailure: state.laboratoryConfidentialityDeed.loadState && state.laboratoryConfidentialityDeed.loadState.status == RequestState.Failure,
        savingLaboratoryConfidentialityDeed: state.laboratoryConfidentialityDeed.saveState && state.laboratoryConfidentialityDeed.saveState.status == RequestState.Pending,
        saveLaboratoryConfidentialityDeedSuccess: state.laboratoryConfidentialityDeed.saveState && state.laboratoryConfidentialityDeed.saveState.status == RequestState.Success,
        saveLaboratoryConfidentialityDeedFailure: state.laboratoryConfidentialityDeed.saveState && state.laboratoryConfidentialityDeed.saveState.status == RequestState.Failure,
        validatingLaboratoryConfidentialityDeed: state.laboratoryConfidentialityDeed.validateState && state.laboratoryConfidentialityDeed.validateState.status == RequestState.Pending,
        creatingLaboratoryConfidentialityDeed: state.laboratoryConfidentialityDeed.createState && state.laboratoryConfidentialityDeed.createState.status == RequestState.Pending,
        createLaboratoryConfidentialityDeedSuccess: state.laboratoryConfidentialityDeed.createState && state.laboratoryConfidentialityDeed.createState.status == RequestState.Success,
        createLaboratoryConfidentialityDeedFailure: state.laboratoryConfidentialityDeed.createState && state.laboratoryConfidentialityDeed.createState.status == RequestState.Failure,
        deletingLaboratoryConfidentialityDeed: state.laboratoryConfidentialityDeed.deleteState && state.laboratoryConfidentialityDeed.deleteState.status == RequestState.Pending,
        deleteLaboratoryConfidentialityDeedSuccess:state.laboratoryConfidentialityDeed.deleteState && state.laboratoryConfidentialityDeed.deleteState.status == RequestState.Success,
        laboratoryConfidentialityDeedLookups: state.laboratoryConfidentialityDeed.lookups,
        laboratoryConfidentialityDeedForm: state.reduxForms.laboratoryConfidentialityDeed,
        laboratoryConfidentialityDeedFormState: state.laboratoryConfidentialityDeed.formState,
        laboratoryConfidentialityDeedFormProperties: state.laboratoryConfidentialityDeed.formProperties,
        laboratoryConfidentialityDeedReduxFormState: state.reduxForms.formState.laboratoryConfidentialityDeed,
        laboratoryConfidentialityDeedValidationFailures:
            laboratoryConfidentialityDeed
                ? state.laboratoryConfidentialityDeed.validationFailures
                : undefined,

        user: state.user.data,
        permissions: permissions,
    };
};

const mapDispatchToProps = (dispatch): Partial<LaboratoryContractPageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        setBreadcrumbs: bindActionCreators(NavigationActions.SetBreadcrumbs, dispatch),
        setDrawerItems: bindActionCreators(NavigationActions.SetDrawerItems, dispatch),

        loadLaboratoryContract: bindActionCreators(LaboratoryContractActions.LoadLaboratoryContractByLaboratoryId, dispatch),
        clearLaboratoryContract: bindActionCreators(LaboratoryContractActions.Clear, dispatch),
        saveLaboratoryContract: bindActionCreators(LaboratoryContractActions.SaveLaboratoryContract, dispatch),
        validateLaboratoryContract: bindActionCreators(LaboratoryContractActions.ValidateLaboratoryContract, dispatch),

        loadLaboratoryConfidentialityDeed: bindActionCreators(LaboratoryConfidentialityDeedActions.LoadLaboratoryConfidentialityDeedById, dispatch),
        clearLaboratoryConfidentialityDeed: bindActionCreators(LaboratoryConfidentialityDeedActions.Clear, dispatch),
        saveLaboratoryConfidentialityDeed: bindActionCreators(LaboratoryConfidentialityDeedActions.SaveLaboratoryConfidentialityDeed, dispatch),
        createLaboratoryConfidentialityDeed: bindActionCreators(LaboratoryConfidentialityDeedActions.CreateLaboratoryConfidentialityDeed, dispatch),
        deleteLaboratoryConfidentialityDeed: bindActionCreators(LaboratoryConfidentialityDeedActions.DeleteLaboratoryConfidentialityDeed, dispatch),
        validateLaboratoryConfidentialityDeed: bindActionCreators(LaboratoryConfidentialityDeedActions.ValidateLaboratoryConfidentialityDeed, dispatch)
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(LaboratoryContractPage);
